/**
 * Unify Schedule & Slot models.
 *
 * Schedule represents a provider's availability window for intake booking.
 * Slot is a child of Schedule representing a single bookable time segment.
 *
 * ┌──────────────────────────────────────────────────────────────────────────────────┐
 * │ SCHEDULE                                                                         │
 * ├──────────────────────┬───────────────────────────────────────────────────────────┤
 * │ Field                │ Description                                               │
 * ├──────────────────────┼───────────────────────────────────────────────────────────┤
 * │ active               │ boolean — whether this schedule is accepting bookings     │
 * │ actor[]              │ Reference<Practitioner>[] (and optionally Location)        │
 * │ serviceType[]        │ CodeableConcept — types of appointments offered            │
 * │ planningHorizon      │ Period { start, end } — date range the schedule covers    │
 * └──────────────────────┴───────────────────────────────────────────────────────────┘
 *
 * ┌──────────────────────────────────────────────────────────────────────────────────┐
 * │ SLOT                                                                             │
 * ├──────────────────────┬───────────────────────────────────────────────────────────┤
 * │ Field                │ Description                                               │
 * ├──────────────────────┼───────────────────────────────────────────────────────────┤
 * │ schedule             │ Reference<Schedule> — parent schedule                     │
 * │ status               │ "free" | "busy" | "busy-unavailable" | "busy-tentative"   │
 * │ start                │ ISO datetime — slot start time                            │
 * │ end                  │ ISO datetime — slot end time                              │
 * │ serviceType[]        │ CodeableConcept — appointment types for this slot          │
 * └──────────────────────┴───────────────────────────────────────────────────────────┘
 *
 * Extension summary:
 *   weekly-availability — recurring weekly client-bookable windows (UTC valueTime)
 *
 * Relationships:
 *   - Each Schedule references one Practitioner (and optionally a Location) via actor[]
 *   - Slots reference their parent Schedule
 *   - Appointments are booked against free Slots
 */
import type { Extension, Schedule, Slot, Reference } from '@medplum/fhirtypes';

import { WEEKLY_AVAILABILITY_EXT } from '../constants';

// ═══════════════════════════════════════════════════════════════════════
//  Literal union types
// ═══════════════════════════════════════════════════════════════════════

/** Slot availability statuses. */
export type SlotStatus = 'free' | 'busy' | 'busy-unavailable' | 'busy-tentative';

// ═══════════════════════════════════════════════════════════════════════
//  Schedule getter helpers
// ═══════════════════════════════════════════════════════════════════════

/** Returns the Practitioner actor reference from a Schedule. */
export function getSchedulePractitionerRef(resource: Schedule): Reference | undefined {
  return resource.actor?.find((a) => a.reference?.startsWith('Practitioner/'));
}

/** Returns the Location actor reference from a Schedule (if present). */
export function getScheduleLocationRef(resource: Schedule): Reference | undefined {
  return resource.actor?.find((a) => a.reference?.startsWith('Location/'));
}

/** Returns all actor references. */
export function getScheduleActorRefs(resource: Schedule): Reference[] {
  return resource.actor ?? [];
}

/** Returns whether the schedule is active. */
export function isScheduleActive(resource: Schedule): boolean {
  return resource.active ?? false;
}

// ═══════════════════════════════════════════════════════════════════════
//  Weekly availability extension (Schedule)
// ═══════════════════════════════════════════════════════════════════════

export const DAY_OF_WEEK_CODES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
export type DayOfWeekCode = (typeof DAY_OF_WEEK_CODES)[number];

/** FHIR DaysOfWeek code → English weekday name used on cohort `group-schedule.day-of-week`. */
export const DAY_OF_WEEK_CODE_TO_WEEKDAY_NAME: Record<DayOfWeekCode, string> = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
};

/** Parse a FHIR DaysOfWeek code or English weekday name into a DaysOfWeek code. */
export function parseDayOfWeekCode(value: string | undefined | null): DayOfWeekCode | undefined {
  const raw = value?.trim();
  if (!raw) return undefined;
  const lower = raw.toLowerCase();
  if ((DAY_OF_WEEK_CODES as readonly string[]).includes(lower)) {
    return lower as DayOfWeekCode;
  }
  const entry = (Object.entries(DAY_OF_WEEK_CODE_TO_WEEKDAY_NAME) as [DayOfWeekCode, string][]).find(
    ([, name]) => name.toLowerCase() === lower,
  );
  return entry?.[0];
}

export type WeeklyAvailabilityTimeWindow = {
  /** UTC time as HH:MM:SS (FHIR valueTime). */
  start: string;
  end: string;
};

/** At most two windows per day in the extension schema; UI currently uses one. */
export type WeeklyAvailabilityPattern = Partial<Record<DayOfWeekCode, WeeklyAvailabilityTimeWindow[]>>;

const DAY_CODE_TO_REF_UTC: Record<DayOfWeekCode, { y: number; m: number; d: number }> = {
  sun: { y: 2024, m: 1, d: 7 },
  mon: { y: 2024, m: 1, d: 8 },
  tue: { y: 2024, m: 1, d: 9 },
  wed: { y: 2024, m: 1, d: 10 },
  thu: { y: 2024, m: 1, d: 11 },
  fri: { y: 2024, m: 1, d: 12 },
  sat: { y: 2024, m: 1, d: 13 },
};

/** Provider Schedule planning horizon — indefinite forward from save instant. */
export function buildProviderPlanningHorizon(): { start: string } {
  return { start: new Date().toISOString() };
}

function parseValueTime(valueTime: string | undefined): { hour: number; minute: number; second: number } | null {
  if (!valueTime) return null;
  const match = valueTime.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  return {
    hour: parseInt(match[1], 10),
    minute: parseInt(match[2], 10),
    second: match[3] ? parseInt(match[3], 10) : 0,
  };
}

function formatValueTime(hour: number, minute: number, second = 0): string {
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`;
}

const ZONED_PARTS_FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>();

function getZonedPartsFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = ZONED_PARTS_FORMATTER_CACHE.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    ZONED_PARTS_FORMATTER_CACHE.set(timeZone, formatter);
  }
  return formatter;
}

function readZonedParts(
  ms: number,
  timeZone: string,
): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const parts = getZonedPartsFormatter(timeZone).formatToParts(new Date(ms));
  const num = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? NaN);
  return {
    year: num('year'),
    month: num('month'),
    day: num('day'),
    hour: num('hour'),
    minute: num('minute'),
    second: num('second'),
  };
}

/** Wall clock in `timeZone` on a reference day → UTC valueTime. */
export function localWallTimeToUtcValueTime(
  dayCode: DayOfWeekCode,
  hour: number,
  minute: number,
  timeZone: string,
): string {
  const ref = DAY_CODE_TO_REF_UTC[dayCode];
  const dayStart = Date.UTC(ref.y, ref.m - 1, ref.d, 0, 0, 0);
  const dayEnd = dayStart + 48 * 60 * 60 * 1000;
  for (let ms = dayStart; ms < dayEnd; ms += 60_000) {
    const zoned = readZonedParts(ms, timeZone);
    if (
      zoned.year === ref.y &&
      zoned.month === ref.m &&
      zoned.day === ref.d &&
      zoned.hour === hour &&
      zoned.minute === minute
    ) {
      const utc = new Date(ms);
      return formatValueTime(utc.getUTCHours(), utc.getUTCMinutes(), utc.getUTCSeconds());
    }
  }
  const fallback = new Date(Date.UTC(ref.y, ref.m - 1, ref.d, hour, minute, 0));
  return formatValueTime(fallback.getUTCHours(), fallback.getUTCMinutes(), fallback.getUTCSeconds());
}

/** UTC valueTime → wall clock in `timeZone` on a reference day (hour/minute only). */
export function utcValueTimeToLocalWallTime(
  dayCode: DayOfWeekCode,
  valueTime: string,
  timeZone: string,
): { hour: number; minute: number } | null {
  const parsed = parseValueTime(valueTime);
  if (!parsed) return null;
  const ref = DAY_CODE_TO_REF_UTC[dayCode];
  const utcMs = Date.UTC(ref.y, ref.m - 1, ref.d, parsed.hour, parsed.minute, parsed.second);
  const zoned = readZonedParts(utcMs, timeZone);
  return { hour: zoned.hour, minute: zoned.minute };
}

function readWindowExtension(windowExt: Extension | undefined): WeeklyAvailabilityTimeWindow | null {
  if (!windowExt) return null;
  const start = windowExt.extension?.find((e) => e.url === 'start')?.valueTime;
  const end = windowExt.extension?.find((e) => e.url === 'end')?.valueTime;
  if (!start || !end) return null;
  return { start, end };
}

function buildWindowExtension(window: WeeklyAvailabilityTimeWindow): Extension {
  return {
    url: 'window',
    extension: [
      { url: 'start', valueTime: window.start },
      { url: 'end', valueTime: window.end },
    ],
  };
}

function buildDayAvailabilityExtension(dayCode: DayOfWeekCode, windows: WeeklyAvailabilityTimeWindow[]): Extension {
  return {
    url: WEEKLY_AVAILABILITY_EXT,
    extension: [
      { url: 'dayOfWeek', valueCode: dayCode },
      ...windows.map(buildWindowExtension),
    ],
  };
}

/** Read weekly-availability extensions from a Schedule. */
export function getWeeklyAvailability(schedule: Schedule): WeeklyAvailabilityPattern {
  const pattern: WeeklyAvailabilityPattern = {};
  for (const ext of schedule.extension ?? []) {
    if (ext.url !== WEEKLY_AVAILABILITY_EXT) continue;
    const dayCode = ext.extension?.find((e) => e.url === 'dayOfWeek')?.valueCode as DayOfWeekCode | undefined;
    if (!dayCode || !DAY_OF_WEEK_CODES.includes(dayCode)) continue;
    const windows =
      ext.extension
        ?.filter((e) => e.url === 'window')
        .map(readWindowExtension)
        .filter((w): w is WeeklyAvailabilityTimeWindow => w !== null) ?? [];
    if (windows.length > 0) {
      pattern[dayCode] = windows;
    }
  }
  return pattern;
}

/** Replace weekly-availability extensions; preserve other extensions. */
export function setWeeklyAvailability(schedule: Schedule, pattern: WeeklyAvailabilityPattern): Schedule {
  const other = (schedule.extension ?? []).filter((e) => e.url !== WEEKLY_AVAILABILITY_EXT);
  const weekly: Extension[] = [];
  for (const dayCode of DAY_OF_WEEK_CODES) {
    const windows = pattern[dayCode];
    if (windows?.length) {
      weekly.push(buildDayAvailabilityExtension(dayCode, windows));
    }
  }
  return {
    ...schedule,
    extension: [...other, ...weekly],
  };
}

/** Apply indefinite provider planning horizon (start = now, no end). */
export function applyProviderPlanningHorizon(schedule: Schedule): Schedule {
  return {
    ...schedule,
    planningHorizon: buildProviderPlanningHorizon(),
  };
}

/** Minimal active Schedule for a Location room (calendar room booking). */
export function buildLocationSchedule(locationId: string, locationDisplay?: string): Schedule {
  return {
    resourceType: 'Schedule',
    active: true,
    actor: [{ reference: `Location/${locationId}`, display: locationDisplay }],
  };
}

/** Align with intake booking + calendar authoring defaults (America/Denver). */
export const DEFAULT_PROVIDER_WEEKLY_AVAILABILITY_TIMEZONE = 'America/Denver';

const DEFAULT_PROVIDER_WEEKDAY_CODES: DayOfWeekCode[] = ['mon', 'tue', 'wed', 'thu', 'fri'];

/** Mon–Fri 9:00–18:00 local — used by seed scripts and provider Schedule bootstrap. */
export function buildDefaultProviderWeeklyAvailabilityPattern(
  timeZone = DEFAULT_PROVIDER_WEEKLY_AVAILABILITY_TIMEZONE,
): WeeklyAvailabilityPattern {
  const pattern: WeeklyAvailabilityPattern = {};
  for (const dayCode of DEFAULT_PROVIDER_WEEKDAY_CODES) {
    pattern[dayCode] = [
      {
        start: localWallTimeToUtcValueTime(dayCode, 9, 0, timeZone),
        end: localWallTimeToUtcValueTime(dayCode, 18, 0, timeZone),
      },
    ];
  }
  return pattern;
}

/** Minimal active Schedule for a provider practitioner. */
export function buildProviderSchedule(practitionerId: string, practitionerDisplay?: string): Schedule {
  return setWeeklyAvailability(
    applyProviderPlanningHorizon({
      resourceType: 'Schedule',
      active: true,
      actor: [{ reference: `Practitioner/${practitionerId}`, display: practitionerDisplay }],
    }),
    buildDefaultProviderWeeklyAvailabilityPattern(),
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  Slot getter helpers
// ═══════════════════════════════════════════════════════════════════════

/** Returns the parent Schedule reference from a Slot. */
export function getSlotScheduleRef(slot: Slot): Reference | undefined {
  return slot.schedule;
}

/** Returns the Slot status. */
export function getSlotStatus(slot: Slot): SlotStatus | undefined {
  return slot.status as SlotStatus | undefined;
}

/** Returns the Slot start time as an ISO string. */
export function getSlotStart(slot: Slot): string | undefined {
  return slot.start;
}

/** Returns the Slot end time as an ISO string. */
export function getSlotEnd(slot: Slot): string | undefined {
  return slot.end;
}

/** Returns whether a Slot is bookable (status = "free"). */
export function isSlotFree(slot: Slot): boolean {
  return slot.status === 'free';
}
